import type { ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { getAPIBaseURL, getAdminHeaders } from "@/lib/auth";
import { cn } from "@/lib/utils";

type UserStats = {
  publications: number;
  conversations: number;
  messages_sent: number;
  invitations_sent: number;
  invitations_received: number;
  reports_received: number;
  reports_made: number;
  payments_completed: number;
  refund_requests: number;
  withdrawals: number;
  deposits_completed: number;
};

type UserDetail = {
  id: string;
  email: string;
  name: string;
  role: "admin" | "user";
  status: "active" | "deleted";
  latest_activity: string | null;
  created_at: string;
  phone_number: string | null;
  date_of_birth: string | null;
  gender: string | null;
  location: string | null;
  bio: string | null;
  photo_url: string | null;
  is_email_verified: boolean;
  is_phone_verified: boolean;
  onboarding_completed_step: number;
  diamonds: number;
  is_online: boolean;
  kyc_status: "none" | "approved" | "rejected";
  kyc_reviewed_at: string | null;
  stats: UserStats;
};

const STAT_LABELS: { key: keyof UserStats; label: string }[] = [
  { key: "publications", label: "Publications" },
  { key: "conversations", label: "Conversations" },
  { key: "messages_sent", label: "Messages sent" },
  { key: "invitations_sent", label: "Invitations sent" },
  { key: "invitations_received", label: "Invitations received" },
  { key: "payments_completed", label: "Completed payments" },
  { key: "deposits_completed", label: "Completed deposits" },
  { key: "refund_requests", label: "Refund requests" },
  { key: "withdrawals", label: "Withdrawals" },
  { key: "reports_received", label: "Reports received" },
  { key: "reports_made", label: "Reports made" },
];

function initials(name: string, email: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return (email[0] ?? "U").toUpperCase();
  if (parts.length === 1) return parts[0]!.slice(0, 2).toUpperCase();
  return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
}

function formatDate(value: string | null, withTime = false) {
  if (!value) return "—";
  const options: Intl.DateTimeFormatOptions = withTime
    ? { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }
    : { day: "2-digit", month: "short", year: "numeric" };
  return new Date(value).toLocaleString("en-GB", options);
}

function yesNo(value: boolean) {
  return value ? "Yes" : "No";
}

function Field({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-6 py-3">
      <dt className="text-sm text-muted-foreground">{label}</dt>
      <dd className="text-right text-sm font-medium text-foreground">{value || "—"}</dd>
    </div>
  );
}

function Pill({ children, tone }: { children: ReactNode; tone: "success" | "muted" | "primary" }) {
  return (
    <span
      className={cn(
        "inline-flex rounded-full px-2.5 py-1 text-xs font-medium",
        tone === "success" && "bg-success-soft text-success",
        tone === "muted" && "bg-muted text-muted-foreground",
        tone === "primary" && "bg-primary-soft text-primary"
      )}
    >
      {children}
    </span>
  );
}

export function UserDetailPage({ userId, onBack }: { userId: string; onBack: () => void }) {
  const user = useQuery({
    queryKey: ["user", userId],
    queryFn: async () => {
      const response = await fetch(`${getAPIBaseURL()}/api/v1/admin/users/${userId}`, { headers: getAdminHeaders() });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.message ?? `Request failed (${response.status})`);
      return payload as UserDetail;
    },
  });

  return (
    <div className="space-y-6">
      <Button variant="outline" size="sm" className="rounded-xl" onClick={onBack}>
        <ArrowLeft className="size-4" />
        Back to users
      </Button>

      {user.isLoading && (
        <div className="space-y-4">
          <Skeleton className="h-20 w-full rounded-2xl" />
          <Skeleton className="h-64 w-full rounded-2xl" />
        </div>
      )}

      {user.isError && (
        <Card className="rounded-2xl border-destructive/40">
          <CardContent className="flex flex-col gap-3 p-5 text-sm text-destructive sm:flex-row sm:items-center sm:justify-between">
            <span>This user could not be loaded. {user.error instanceof Error ? user.error.message : ""}</span>
            <Button variant="outline" size="sm" className="rounded-xl" onClick={() => user.refetch()}>
              Try again
            </Button>
          </CardContent>
        </Card>
      )}

      {user.data && <UserDetailContent user={user.data} />}
      {user.data && <KycCard user={user.data} />}
    </div>
  );
}

const KYC_LABELS: Record<UserDetail["kyc_status"], string> = {
  none: "Not approved",
  approved: "Approved",
  rejected: "Rejected",
};

/** KYC decision. Only an approved KYC shows the blue tick on the person's posts and profile. */
function KycCard({ user }: { user: UserDetail }) {
  const queryClient = useQueryClient();
  const decide = useMutation({
    mutationFn: async (status: UserDetail["kyc_status"]) => {
      const response = await fetch(`${getAPIBaseURL()}/api/v1/admin/users/${user.id}/kyc`, {
        method: "PATCH",
        headers: getAdminHeaders(),
        body: JSON.stringify({ status }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(payload.message ?? "Could not update KYC");
    },
    onSuccess: (_data, status) => {
      toast.success(status === "approved" ? "KYC approved: blue tick is now shown" : status === "rejected" ? "KYC rejected" : "KYC reset");
      queryClient.invalidateQueries({ queryKey: ["user", user.id] });
    },
    onError: (error: Error) => toast.error(error.message),
  });

  return (
    <Card className="rounded-2xl">
      <CardContent className="flex flex-col gap-4 p-6 sm:flex-row sm:items-center sm:justify-between">
        <div className="space-y-1">
          <p className="text-xs font-semibold uppercase text-muted-foreground">KYC and blue tick</p>
          <p className="text-lg font-semibold">{KYC_LABELS[user.kyc_status]}</p>
          <p className="text-sm text-muted-foreground">
            {user.kyc_status === "approved"
              ? "The blue tick shows on this person's posts and profile."
              : "No blue tick is shown until KYC is approved."}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {user.kyc_status !== "approved" && (
            <Button className="rounded-xl" disabled={decide.isPending} onClick={() => decide.mutate("approved")}>
              Approve KYC
            </Button>
          )}
          {user.kyc_status !== "rejected" && (
            <Button variant="outline" className="rounded-xl" disabled={decide.isPending} onClick={() => decide.mutate("rejected")}>
              Reject
            </Button>
          )}
          {user.kyc_status !== "none" && (
            <Button variant="ghost" className="rounded-xl" disabled={decide.isPending} onClick={() => decide.mutate("none")}>
              Reset
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function UserDetailContent({ user }: { user: UserDetail }) {
  return (
    <>
      <Card className="rounded-2xl">
        <CardContent className="flex flex-col gap-5 p-6 sm:flex-row sm:items-center">
          {user.photo_url ? (
            <img src={user.photo_url} alt="" className="size-16 shrink-0 rounded-full object-cover" />
          ) : (
            <div className="flex size-16 shrink-0 items-center justify-center rounded-full bg-primary-soft text-lg font-semibold text-primary">
              {initials(user.name, user.email)}
            </div>
          )}
          <div className="min-w-0 flex-1 space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="text-2xl font-semibold text-foreground">{user.name || "Unnamed user"}</h2>
              <Pill tone={user.status === "active" ? "success" : "muted"}>{user.status === "active" ? "Active" : "Deleted"}</Pill>
              <Pill tone={user.role === "admin" ? "primary" : "muted"}>{user.role === "admin" ? "Admin" : "User"}</Pill>
              {user.is_online && <Pill tone="success">Online</Pill>}
            </div>
            <p className="break-all text-sm text-muted-foreground">{user.email}</p>
            <p className="break-all font-mono text-xs text-muted-foreground">{user.id}</p>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="rounded-2xl">
          <CardHeader className="border-b p-5">
            <h3 className="font-semibold">Profile</h3>
          </CardHeader>
          <CardContent className="px-5">
            <dl className="divide-y">
              <Field label="Name" value={user.name} />
              <Field label="Email" value={user.email} />
              <Field label="Phone" value={user.phone_number} />
              <Field label="Date of birth" value={formatDate(user.date_of_birth)} />
              <Field label="Gender" value={user.gender} />
              <Field label="Location" value={user.location} />
              <Field label="Bio" value={user.bio ? <span className="whitespace-pre-line">{user.bio}</span> : null} />
            </dl>
          </CardContent>
        </Card>

        <Card className="rounded-2xl">
          <CardHeader className="border-b p-5">
            <h3 className="font-semibold">Account</h3>
          </CardHeader>
          <CardContent className="px-5">
            <dl className="divide-y">
              <Field label="Status" value={user.status === "active" ? "Active" : "Deleted"} />
              <Field label="Role" value={user.role === "admin" ? "Admin" : "User"} />
              <Field label="Email verified" value={yesNo(user.is_email_verified)} />
              <Field label="Phone verified" value={yesNo(user.is_phone_verified)} />
              <Field label="Onboarding step" value={String(user.onboarding_completed_step)} />
              <Field label="Diamonds" value={user.diamonds.toLocaleString("en-GB")} />
              <Field label="Joined" value={formatDate(user.created_at, true)} />
              <Field label="Last seen" value={formatDate(user.latest_activity, true)} />
            </dl>
          </CardContent>
        </Card>
      </div>

      <Card className="rounded-2xl">
        <CardHeader className="border-b p-5">
          <h3 className="font-semibold">Activity</h3>
          <p className="text-sm text-muted-foreground">Counts from the database for this account.</p>
        </CardHeader>
        <CardContent className="grid grid-cols-2 gap-3 p-5 sm:grid-cols-3 xl:grid-cols-4">
          {STAT_LABELS.map((stat) => (
            <div key={stat.key} className="rounded-xl bg-muted/40 p-4">
              <p className="text-xs text-muted-foreground">{stat.label}</p>
              <p className="mt-1 text-2xl font-semibold tabular-nums">{user.stats[stat.key].toLocaleString("en-GB")}</p>
            </div>
          ))}
        </CardContent>
      </Card>
    </>
  );
}
